import React, { useState, useEffect } from 'react';
import { Card, TextStyle, Link, Collapsible } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  getTranslatedText,
  useIntl,
  getHeaderValue,
  fetchApi,
  DateTimeFormatter,
  toast
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import { useKbContext } from '../../contexts/KbContext';
import { isEmpty } from 'lodash';

const ImportSummary = () => {
  const [openStatus, setOpenStatus] = useState(false);
  const {
    activeViewConfig,
    setLoadingImportSummary,
    setShowImportSummary
  } = useKbContext();
  const [viewInfo, setViewInfo] = useState(null);
  const intl = useIntl();
  const importingId = localStorage.getItem('articleImporting');

  const getImportSummary = async () => {
    if (!activeViewConfig?.activeItemId) return;

    const inputVariables = {
      module: 'KB_ARTICLE',
      status: 'SUCCESS',
      details: JSON.stringify({
        parentId: activeViewConfig?.activeItemId
      })
    };

    const importSummaryApi = `${envConfig.loginRoot}/import/lastImport?module=${
      inputVariables?.module
    }&details=${escape(inputVariables?.details)}`;

    const requestOptions = {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...getHeaderValue(),
        'Content-Type': 'application/json'
      }
    };

    try {
      setLoadingImportSummary(true);
      const response = await fetchApi(importSummaryApi, requestOptions);
      const responseData = await response.json();

      if (responseData?.data) {
        if (importingId === responseData?.data?.importId?.toString()) {
          localStorage.removeItem('articleImporting');
        }
        const successCount =
          responseData.data?.files?.filter((file) => file.status === 'SUCCESS')
            .length || 0;

        const failedFiles =
          responseData.data?.files?.filter(
            (file) => file.status === 'FAILURE'
          ) || [];
        const failureCount = failedFiles.length || 0;

        setViewInfo({
          data: responseData.data,
          successCount,
          failureCount,
          failedFiles
        });
        if (isEmpty(responseData.data)) {
          toast('error', 'app.kb.importSummary.lastImport.empty.message');
          setShowImportSummary(false);
        } else {
          toast('success', 'app.kb.importSummary.lastImport.success.message');
        }
      }
    } catch (err) {
      console.error('Import summary error:', err);
    } finally {
      setLoadingImportSummary(false);
    }
  };

  useEffect(() => {
    if (!isEmpty(activeViewConfig)) {
      getImportSummary();
    }
  }, [activeViewConfig?.activeItemId]);

  const summaryListDetails = [
    {
      icon: <SVGIcon name="Refresh" />,
      message: (
        <TextStyle weight="SemiBold">
          {getTranslatedText(intl, 'app.kb.lastImport.label')} {' - '}
          {viewInfo?.data?.completedOn ? (
            <DateTimeFormatter
              type="dateTime"
              date={viewInfo?.data?.completedOn}
            />
          ) : (
            '-'
          )}
        </TextStyle>
      )
    },
    {
      icon: <SVGIcon name="SuccessCircle" />,
      message: (
        <TextStyle>
          {viewInfo?.successCount ?? 0}{' '}
          {getTranslatedText(intl, 'app.kb.import.article.success.label')}
        </TextStyle>
      )
    },
    {
      icon: <SVGIcon name="RejectedCircle" color="#E72F23" />,
      message: (
        <div className="fx g40">
          <TextStyle>
            {viewInfo?.failureCount ?? 0}{' '}
            {getTranslatedText(intl, 'app.kb.import.article.failed.label')}
          </TextStyle>
          {viewInfo?.failureCount > 0 && (
            <div>
              <Link onClick={() => setOpenStatus(!openStatus)}>
                {openStatus
                  ? getTranslatedText(intl, 'common.label.hide')
                  : getTranslatedText(intl, 'admin.technicians.import.viewAll')}
              </Link>
            </div>
          )}
        </div>
      )
    }
  ];

  if (isEmpty(viewInfo?.data)) return null;

  return (
    <div className="p20">
      <Card variant="secondaryCard" noShadow>
        <div className="fx col g10 pl10 pt20 pr10 pb20 relative">
          <div
            onClick={() => setShowImportSummary(false)}
            className="cursor-pointer close-btn-card"
          >
            <SVGIcon name="Close" />
          </div>
          {summaryListDetails.map((item, index) => (
            <div key={index} className="fx actr g40">
              {item.icon}
              {item.message}
            </div>
          ))}
          <Collapsible open={openStatus}>
            <div className="mt20 ml-50 mb20">
              <div className="grid pl10 pr10 grid-1fr-1fr">
                <TextStyle variation="subdued" uppercase>
                  {getTranslatedText(intl, 'app.kb.document.name.label')}
                </TextStyle>
                <TextStyle variation="subdued" uppercase>
                  {getTranslatedText(intl, 'common.error.message')}
                </TextStyle>
              </div>
              <div className="grid g8 p10">
                {viewInfo?.failedFiles?.map((file, index) => (
                  <div key={index} className="grid grid-1fr-1fr">
                    <TextStyle weight="SemiBold">
                      {file.originalFileName}
                    </TextStyle>
                    <div className="fx actr g5">
                      <SVGIcon name="RejectedCircle" color="#E72F23" />
                      <TextStyle weight="SemiBold">
                        {getTranslatedText(
                          intl,
                          `app.errorCode.${file.errors?.[0]?.errorCode}`
                        )}
                      </TextStyle>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Collapsible>
        </div>
      </Card>
    </div>
  );
};

export default ImportSummary;
